import React, { useState, useEffect } from 'react'; 
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../utils/AuthContext/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import config from '../utils/config';
import SEO from '../utils/SEO';
import { Turnstile } from '@marsidev/react-turnstile';

import GoogleLoginButton from '../components/GoogleLoginButton';
import MainCard from '../components/MainCard';

function Login() {
    const [userData, setUserData] = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [turnstileToken, setTurnstileToken] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const navigate = useNavigate();
    const { login, user } = useAuth(); 

    useEffect(() => {
        if (user) {
            navigate('/');
        }
    }, [user, navigate]);

    // Cleanup effect
    useEffect(() => {
        return () => {
            setTurnstileToken('');
            setError('');
        };
    }, []);

    const handleChange = (e) => {
        setUserData({ ...userData, [e.target.name]: e.target.value }); 
    }; 

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (import.meta.env.VITE_ENABLE_TURNSTILE === 'true' && !turnstileToken) {
            setError('Please complete the Turnstile verification');
            return;
        }
        
        setIsVerifying(true);
        try {
            const response = await fetch(`${config.apiBaseUrl}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    email: userData.email, 
                    password: userData.password,
                    turnstileToken: import.meta.env.VITE_ENABLE_TURNSTILE === 'true' ? turnstileToken : 'dev-mode'
                }),
                credentials: 'include'
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Unknown login error');
            }

            console.log('User Successfully Logged In');
            login(data.token, data.user);
            navigate('/');
        } catch (error) {
            console.error('Login failed:', error.message);
            setError(error.message);
        } finally {
            setIsVerifying(false);
        }
    };

    return (
        <>
            <SEO title="Login" description="Login to your Shishito account to start building and managing real estate offers and the steps leading to closing." />

            <div className="flex-1 flex justify-center items-center">
                <div className="h-[450px] w-full md:h-[750px] md:w-[750px] flex flex-col justify-start items-center">
                    <MainCard maxHeight={475}>
                        <h1 className="items-center justify-start text-black text-[24px] md:text-[34px] w-full font-semibold font-['Inter'] mb-3">Sign In</h1>

                        <form onSubmit={handleSubmit} className='w-[300px] md:w-[450px] h-auto justify-between gap-6 items-start flex flex-col'>
                            <div className="relative flex flex-col mb-1 w-full border rounded-md border-slate-400 p-1 focus-within:ring-1 focus-within:ring-blue-800">
                                <span className="absolute -top-3 left-2 bg-white px-1 text-[12px] font-[Inter] text-slate-500">Email</span>
                                <input 
                                    className='w-full border-none outline-none pt-1'
                                    type="email" 
                                    name="email" 
                                    value={userData.email} 
                                    onChange={handleChange} 
                                    placeholder="" 
                                />
                            </div>

                            <div className="relative flex flex-col mb-1 w-full border rounded-md border-slate-400 p-1 focus-within:ring-1 focus-within:ring-blue-800">
                                <span className="absolute -top-3 left-2 bg-white px-1 text-[12px] font-[Inter] text-slate-500">Password</span>
                                <input 
                                    className='w-full border-none outline-none pt-1'
                                    type="password" 
                                    name="password" 
                                    value={userData.password} 
                                    onChange={handleChange} 
                                    placeholder="" 
                                />
                            </div>

                            {import.meta.env.VITE_ENABLE_TURNSTILE === 'true' && (
                                <div className="w-full flex justify-center">
                                    <Turnstile
                                        siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                                        onSuccess={(token) => {
                                            console.log('Turnstile verification successful');
                                            setTurnstileToken(token);
                                        }}
                                        onError={(error) => {
                                            console.error('Turnstile error:', error);
                                            setError('Turnstile verification failed');
                                        }}
                                        onExpire={() => {
                                            console.log('Turnstile expired');
                                            setTurnstileToken('');
                                        }}
                                        options={{
                                            refreshExpired: 'manual',
                                            appearance: 'always',
                                            language: 'auto',
                                            size: 'normal',
                                            theme: 'light',
                                            autoResetOnTimeout: false
                                        }}
                                    />
                                </div>
                            )}

                            <div className="w-full h-[35px] md:h-[41px] mt-5 justify-center inline-flex">
                                <button 
                                    className="w-[100px] md:w-[150px] justify-center items-center text-white bg-shi-green border-shi-green text-sm md:text-lg font-normal font-['Inter'] p-[1px] md:p-1.5 rounded-md border hover:bg-green-950 flex disabled:opacity-50" 
                                    type="submit"
                                    disabled={isVerifying}>
                                    {isVerifying ? 'Verifying...' : 'Sign In'}
                                </button>
                            </div>
                            {error && <p className='text-red-700 text-semibold'>{error}</p>}
                        </form>

                        <div className="my-4 flex items-center justify-center w-full">
                            <div className="border-t border-gray-300 flex-grow"></div>
                            <span className="px-4 text-gray-500">or</span>
                            <div className="border-t border-gray-300 flex-grow"></div>
                        </div>

                        <GoogleLoginButton />

                        <Link 
                            to="/register"
                            className="w-[300px] md:w-[350px] justify-center text-center text-shi-green bg-white border-white text-sm md:text-lg font-normal font-['Inter'] p-1.5 rounded-md border hover:bg-slate-200 items-start flex"
                            >New to Shishito? Create Account
                        </Link>
                        <Link 
                            to="/forgot-password"
                            className="w-[300px] md:w-[350px] justify-center text-center text-shi-green bg-white border-white text-sm md:text-lg font-normal font-['Inter'] p-1.5 rounded-md border hover:bg-slate-200 items-start flex"
                            >Forgot Password?
                        </Link>
                    </MainCard>
                </div>
            </div>
        </>
    );
}

export default Login;